import { Category } from '@/domain/models/content/category'
import { EnrollmentCategory } from '@/domain/models/content/enrollment-category'
import { i18nTranslationHelper } from '@/i18n'
import getCompanyManagerPaths from '@/main/routes/paths/company-manager-paths'
import getUserDataPaths from '@/main/routes/paths/user-data-paths'
import { makeSkillWebManagerUsersRedirectUrl } from '../factories/http'
import { computeContentValueId } from './paths/compute-path-helper'
import getContentDiscoveryPaths from './paths/content-discovery-paths'
import getInternalCompanyManagerPaths from './paths/internal-company-manager-paths'
import getOnboardingPaths from './paths/onboarding-paths'

const t = i18nTranslationHelper.getTPage

export namespace PathConstants {
  export type RoutesProps = {
    [key: string]: RouteProps
  }

  export type RouteProps = {
    value: string
    name: string
    title?: string
    computeValue?: (args: { [key: string]: string | number | undefined | unknown }) => string
    computeTitle?: (args: { [key: string]: string | number | undefined | unknown }) => string
  }

  export const findRouteByPathname = (pathname: string): RouteProps | null => {
    let route = null
    Object.entries(Routes).forEach(([key, value]) => {
      if (value.value === pathname) {
        route = PathConstants.Routes[key]
      }
    })
    return route
  }

  export const findRouteByCategory = (category: Category.Enum) => {
    return PathConstants.Routes[`CATEGORY_${category.name.toUpperCase()}`]
  }

  export const getCategoryRoutePath = (category: Category.Enum) => {
    return findRouteByCategory(category).value.toLocaleLowerCase()
  }

  export const findRouteByCategoryGroup = (group: EnrollmentCategory.Enum) => {
    return PathConstants.Routes[`CATEGORY_GROUP_${group.name.toUpperCase()}`]
  }

  export const getCategoryGroupRoutePath = (group: EnrollmentCategory.Enum) => {
    return findRouteByCategoryGroup(group).value.toLocaleLowerCase()
  }

  export const Routes: RoutesProps = {
    ...getOnboardingPaths(),
    ...getContentDiscoveryPaths(),
    ...getCompanyManagerPaths(),
    ...getInternalCompanyManagerPaths(),
    ...getUserDataPaths(),
    CONTENT_ENROLLMENT: {
      value: '/content/:id/enrollment',
      name: 'content_enrollment',
      computeValue: ({ content }) => `/content/${computeContentValueId(content)}/enrollment`
    },
    CONTENT_UNSUBSCRIBE: {
      value: '/content/enrollment/:id/unsubscribe',
      name: 'content_unsubscribe',
      computeValue: args => `/content/enrollment/${args.id}/unsubscribe`
    },
    CONTENT_COMPLETE: {
      value: '/content/enrollment/:id/complete',
      name: 'content_complete',
      computeValue: args => `/content/enrollment/${args.id}/complete`
    },
    CBU_START: {
      value: '/cbu/start',
      name: 'cbu_start'
    },
    PROFILE_ASSESSMENT_CALLBACK: {
      value: '/assessment/callback',
      name: 'profile_assessment_callback'
    },
    PROFILE_ASSESSMENT_IN_PROGRESS: {
      value: '/assessment/progress',
      name: 'profile_assessment_in_progress'
    },
    PROFILE_ASSESSMENT_GET_STARTED: {
      value: '/assessment/get-started',
      name: 'profile_assessment_get_started'
    },
    REFUND: {
      value: '/refund',
      name: 'refund_program_list',
      title: t('refund.title')
    },
    REFUND_REQUEST: {
      value: '/refund/program/:id/request',
      name: 'refund_request',
      computeValue: args => `/refund/program/${args.id}/request`
    },
    UPGRADE_REQUEST: {
      value: '/upgrade-request',
      name: 'upgrade_request'
    },
    DEPENDENT_REQUEST: {
      value: '/dependent-request',
      name: 'dependent_request'
    },
    DEPENDENT_REQUEST_CATALOG: {
      value: '/dependent-request/catalog',
      name: 'dependent_request_catalog',
      title: t('dependentRequest.catalog.pageTitle')
    },
    DEPENDENT_REQUEST_CONTENT: {
      value: '/dependent-request/catalog/:id',
      name: 'dependent_request_content',
      computeValue: ({ content }) => `/dependent-request/catalog/${computeContentValueId(content)}`
    },
    CONTACT_VERIFICATION: {
      value: '/contact-verification',
      name: 'contact_verification',
      title: t('contactVerification.title')
    }
  }

  export const StaticRoutes: RoutesProps = {
    CB_UNICO_SUCCESS: {
      value: '/cbu/success',
      name: 'cbu_success'
    },
    FIRST_ACCESS: {
      value: '/first-access',
      name: 'first_access'
    },
    FIRST_ACCESS_CPF: {
      value: '/first-access/cpf',
      name: 'first_access_cpf'
    }
  }

  export const ExternalRoutes: RoutesProps = {
    MONOLITH_MANAGE_CUSTOMER: {
      name: 'monolith_manage_customers',
      value: makeSkillWebManagerUsersRedirectUrl(),
      title: t('externalRoutes.monolithManageCustomer.pageTitle')
    }
  }

  export const isInStaticRoute = () =>
    Object.values(PathConstants.StaticRoutes).some(
      route => route.value === window.location.pathname
    )

  Routes.INDEX = Routes.HOME
}

export default PathConstants
